<template>
  <div v-if="show" v-rect-loading="loading" class="job-post-channel">
    <div ref="channelList" class="channel-list">
      <div
        v-for="channel in channelList"
        class="channel"
        :key="channel.id"
        :class="getPostStatusClass(channel)"
      >
        <div class="channel-wrapper">
          <i class="iconfont" :class="['channel-icon', CHANNEL_ICON_MAPPING[`channel_${channel.key}`]]"/>
          <i :class="['status-icon', channel.status || channel.originStatus]"></i>
          <el-dropdown class="post-btn-dropdown-container" trigger="click" placement="bottom-start" :disabled="disabled || stepData[channel.id]?.isPending" @command="handelChangeChannelPostStatus($event, channel)">
            <span class="operate-icon iconfont icona-xingzhuangbeifen8"></span>
            <el-dropdown-menu class="channel-operation-container" slot="dropdown">
              <template v-if="channel.isDefault">
                <el-dropdown-item v-if="getPostOperationType(channel) === OPERATION_TYPE.ADD" class="post-btn hidden" :command="CHANNEL_ACTION.HIDE">{{ $t(`${i18nPrefix}.btn.postingChannel.hidden`) }}</el-dropdown-item>
                <el-dropdown-item v-else class="post-btn public" :command="CHANNEL_ACTION.ADD">{{ $t(`${i18nPrefix}.btn.postingChannel.autoPost`) }}</el-dropdown-item>
              </template>
              <template v-else>
                <el-dropdown-item v-if="getPostStatus(channel) === JOB_POSTING_STATUS.NEW" class="post-btn cancel" :command="CHANNEL_ACTION.CANCEL">{{ $t(`${i18nPrefix}.btn.postingChannel.cancel`) }}</el-dropdown-item>
                <template v-else-if="getPostStatus(channel) === JOB_POSTING_STATUS.FINISHED">
                  <el-dropdown-item v-if="getLink(channel)" class="post-btn" :command="CHANNEL_ACTION.VIEW">{{ $t(`common.btn.view`) }}</el-dropdown-item>
                  <el-dropdown-item class="post-btn update" :command="CHANNEL_ACTION.UPDATE">{{ $t(`${i18nPrefix}.btn.postingChannel.update`) }}</el-dropdown-item>
                  <el-dropdown-item class="post-btn delete" :command="CHANNEL_ACTION.HIDE">{{ $t(`${i18nPrefix}.btn.postingChannel.delete`) }}</el-dropdown-item>
                </template>
                <el-dropdown-item v-else class="post-btn add" :command="CHANNEL_ACTION.ADD">{{ $t(`${i18nPrefix}.btn.postingChannel.autoPost`) }}</el-dropdown-item>
              </template>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
        <div class="channel-name">{{ channel.i18nName }}</div>
      </div>
    </div>
    <div class="tip-message" v-html="$t(`${i18nPrefix}.message.notFoundPostingChannel`)"></div>
    <div v-show="showAdditionalInfoContainer" class="additional-info-container">
      <div class="container-title">{{ $t(`${i18nPrefix}.label.channelAdditionalInfoTitle`) }}</div>
      <div class="container-body">
        <div
          v-for="(item, index) in additionalInfoList"
          v-show="item.show"
          class="info-editor"
          :key="index"
        >
          <div class="channel-name-container">
            <div class="channel-icon" :class="`channel-${item.channel.key}`"></div>
            <div class="channel-name">{{ item.channel.i18nName }}</div>
          </div>
          <div class="additional-info">
            <div
              v-for="(line, infoName) in item.infoLines"
              class="info-line"
              :key="infoName"
            >
              <div class="line-label">{{ line.label }}</div>
              <div class="line-content">
                <template v-if="line.isMultiple">
                  <div class="content-item-container">
                    <div
                      v-for="(valueItem, valueIndex) in line.value"
                      :key="valueIndex"
                      class="content-item"
                    >
                      <el-cascader
                        v-if="line.type === 'cascader'"
                        v-model="line.value[valueIndex]"
                        v-bind="line.props"
                        v-on="line.events"
                        :placeholder="line.placeholder"
                        :options="line.optionList"
                        @change="onAdditionalInfoChange"
                      ></el-cascader>
                      <el-input v-else v-model="line.value" :placeholder="line.placeholder"></el-input>
                      <div class="delete-btn" @click="handleLineItemDelete(line, valueItem, valueIndex)">
                        <i class="btn-icon el-icon-close"></i>
                      </div>
                    </div>
                  </div>
                  <el-button v-show="line.maxValueCount ? line.value.length < line.maxValueCount : true" class="add-btn" type="text" @click="handleLineItemAdd(line)">{{ $t(`common.btn.add`) }}</el-button>
                </template>
                <template v-else>
                  <el-cascader
                    v-if="line.type === 'cascader'"
                    v-model="line.value"
                    v-bind="line.props"
                    v-on="line.events"
                    :placeholder="line.placeholder"
                    :options="line.optionList"
                    @change="onAdditionalInfoChange"
                  ></el-cascader>
                  <el-input v-else v-model="line.value" :placeholder="line.placeholder" @change="onAdditionalInfoChange"></el-input>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import mixin from './mixin'
import {
  CUSTOM_FIELD_TYPE, JOB_STATUS_TYPE, DEFAULT_CHANNEL_ID, CHANNEL_ICON_MAPPING, CHANNEL_OPERATION_CANCEL, CHANNEL_OPERATION_VIEW,
  CHANNEL_OPERATION_UPDATE, CHANNEL_OPERATION_HIDE, CHANNEL_OPERATION_ADD
} from '@/constant/job'
import { addI18nProperty, createI18nProperty } from '@/i18n'
import { addRelatedProperty } from '@/utils/common'
import { channelDefaultCountForLinkedin, OPERATION_TYPE, JOB_POSTING_STATUS } from '@/constant/company'

const CHANNEL_ACTION = {
  ADD: CHANNEL_OPERATION_ADD,
  HIDE: CHANNEL_OPERATION_HIDE,
  UPDATE: CHANNEL_OPERATION_UPDATE,
  CANCEL: CHANNEL_OPERATION_CANCEL,
  VIEW: CHANNEL_OPERATION_VIEW
}

const JOB_CHANNEL_ADDITIONAL_INFO_TYPE = {
  INDUSTRY: 'industry',
  THIRD_PARTY_LINK: 'third_party_link'
}

export default {
  mixins: [mixin],
  props: {
    rootCompany: {
      default: null
    }
  },
  data () {
    return {
      JOB_POSTING_STATUS,
      OPERATION_TYPE,
      CHANNEL_ACTION,
      CHANNEL_ICON_MAPPING,
      hasForm: false,
      loading: false,
      channelList: [],
      channelListLoader: null,
      channelAdditionInfoMapLoader: null,
      channelAdditionInfoMap: null,
      additionalInfoList: [],
      hasChangedAdditionInfo: false,
      channelIndustryList: null,
      channelIndustryListLoader: null,
      matchedIndustryOption: {
        loader: null,
        customFiledKey: null,
        matched: null,
        noCustomField: false
      }
    }
  },
  computed: {
    showAdditionalInfoContainer () {
      return _.findIndex(this.additionalInfoList, 'show') > -1
    }
  },
  created () {
    this.loadChannelList()
    this.loadChannelAdditionalInfoMap()
  },
  methods: {
    ...mapActions('company', ['getCompanyJobPostingChannelList']),
    ...mapActions('job', ['getJobChannelAdditionalInfo']),
    ...mapActions('jobPosting', ['searchPostingFieldOptions', 'getCustomFieldMatchedPostingField']),
    async refreshLastChannelEl () {
      if (!this.show) {
        return
      }
      await this.loadChannelList()
      await this.$nextTick()
    },
    async loadChannelAdditionalInfoMap () {
      if (!this.channelAdditionInfoMapLoader) {
        this.channelAdditionInfoMapLoader = this.getJobChannelAdditionalInfo(this.companyDataParams).then(resp => {
          this.channelAdditionInfoMap = resp
        })
      }
      await this.channelAdditionInfoMapLoader
      return this.channelAdditionInfoMap
    },
    async loadChannelIndustryList () {
      if (!this.channelIndustryListLoader) {
        const params = { field_type: 'industry', need_children: true, group_channel: true }
        this.channelIndustryListLoader = this.searchPostingFieldOptions(params).then(resp => {
          _.forEach(resp, industryList => {
            _.forEach(industryList, industryCategory => {
              addI18nProperty(industryCategory, 'label', 'cn_name', 'name')
              addRelatedProperty(industryCategory, 'value', 'id')
              _.forEach(industryCategory.children, industryType => {
                addI18nProperty(industryType, 'label', 'cn_name', 'name')
                addRelatedProperty(industryType, 'value', 'id')
                _.forEach(industryType.children, industryName => {
                  addI18nProperty(industryName, 'label', 'cn_name', 'name')
                  addRelatedProperty(industryName, 'value', 'id')
                })
              })
            })
          })
          this.channelIndustryList = resp
        })
      }
      await this.channelIndustryListLoader
      return this.channelIndustryList
    },
    async loadMatchedIndustryOption () {
      const customFieldMap = {}
      const matchedIndustryOption = this.matchedIndustryOption
      _.forEach(this.jobInfo.custom_fields, field => {
        const fieldId = field.id
        const fieldType = field.type
        const optionIds = field.filter_value_items
        if (![CUSTOM_FIELD_TYPE.SELECT, CUSTOM_FIELD_TYPE.SINGLE].includes(fieldType) || !optionIds || optionIds.length < 1) {
          matchedIndustryOption.matched = {}
          return
        }
        customFieldMap[fieldId] = _.map(optionIds, 'id')
      })
      if (Object.keys(customFieldMap).length < 1) {
        matchedIndustryOption.matched = {}
        matchedIndustryOption.noCustomField = true
        return
      }
      matchedIndustryOption.noCustomField = false
      const loaderKey = `${Object.keys(customFieldMap).join(',')}-${Object.values(customFieldMap).join(',')}`
      if (matchedIndustryOption.loaderKey !== loaderKey || !matchedIndustryOption.loader) {
        matchedIndustryOption.loaderKey = loaderKey
        const params = {
          company_id: this.jobInfo.company.id,
          field_type: 'industry',
          custom_field_map: customFieldMap
        }
        matchedIndustryOption.loader = this.getCustomFieldMatchedPostingField(params).then(resp => {
          matchedIndustryOption.matched = resp
        })
      }
      await matchedIndustryOption.loader
      return matchedIndustryOption.matched
    },
    getPostOperationType (channel) {
      return this.stepData[channel.id]?.operationType
    },
    getPostStatus (channel) {
      return this.stepData[channel.id]?.status
    },
    getPostStatusClass (channel) {
      const postingChannel = this.stepData[channel.id]
      if (!postingChannel) {
        return null
      }
      const status = postingChannel.status
      const operationType = postingChannel.operationType
      let statusClass = 'disabled'
      if (status === JOB_POSTING_STATUS.FINISHED) {
        statusClass = 'finished'
      } else if (operationType === OPERATION_TYPE.ADD) {
        statusClass = 'add'
      } else if (operationType === OPERATION_TYPE.UPDATE) {
        statusClass = 'update'
      } else if (operationType === OPERATION_TYPE.DELETE) {
        statusClass = 'delete'
      }
      if (channel.isDefault) {
        if (this.isEditMode) {
          if (status === JOB_POSTING_STATUS.FINISHED) {
            statusClass = operationType === OPERATION_TYPE.DELETE ? 'disabled' : 'finished'
          } else if (postingChannel.originStatus === JOB_POSTING_STATUS.FINISHED) {
            if (operationType === OPERATION_TYPE.DELETE) {
              statusClass = postingChannel.originOperationType === OPERATION_TYPE.DELETE ? 'disabled' : 'delete'
            } else {
              statusClass = postingChannel.originOperationType === OPERATION_TYPE.ADD ? 'finished' : 'add'
            }
          }
        } else {
          statusClass = operationType === OPERATION_TYPE.DELETE ? 'disabled' : 'add'
        }
      }
      return statusClass
    },
    async loadChannelList () {
      if (!this.channelListLoader) {
        this.channelListLoader = this.getCompanyJobPostingChannelList(this.companyDataParams).then(resp => {
          if (resp && resp.post_channels) {
            this.channelList = _.chain(resp.post_channels)
              .filter(channel => channel.auto_job_posting_can_use).map(channel => {
                if (channel.id === DEFAULT_CHANNEL_ID) {
                  channel.isDefault = true
                  channel.name = 'Career Site'
                  channel.cn_name = '职位主页'
                }
                addI18nProperty(channel, 'i18nName', 'cn_name', 'name')
                return channel
              }).value()
          }
          return this.channelList
        })
      }
      await this.channelListLoader
      return this.channelList
    },
    handleAddChannel (channel) {
      if (channel.has_count !== undefined && channel.has_count === false) {
        const count = channel.count || channelDefaultCountForLinkedin
        this.$message.warning(this.$t('admin.company.jobBoardPointDashboard.message.countsNotEnough', { count }))
        return
      }
      if (channel.has_points !== undefined && channel.has_points === false) {
        this.$message.warning(this.$t('admin.company.jobBoardPointDashboard.message.pointsNotEnough'))
        return
      }
      const postingChannel = this.stepData[channel.id]
      postingChannel.status = JOB_POSTING_STATUS.NEW
      postingChannel.operationType = OPERATION_TYPE.ADD
    },
    handleUpdateChannel (channel) {
      const postingChannel = this.stepData[channel.id]
      postingChannel.status = JOB_POSTING_STATUS.NEW
      postingChannel.operationType = OPERATION_TYPE.UPDATE
    },
    handleDeleteChannel (channel) {
      const postingChannel = this.stepData[channel.id]
      postingChannel.status = JOB_POSTING_STATUS.NEW
      postingChannel.operationType = OPERATION_TYPE.DELETE
    },
    handleCancelChannel (channel) {
      const postingChannel = this.stepData[channel.id]
      postingChannel.status = postingChannel.originStatus
      postingChannel.operationType = postingChannel.originOperationType
    },
    handelChangeChannelPostStatus (option, channel) {
      if (option === CHANNEL_OPERATION_CANCEL) {
        this.handleCancelChannel(channel)
      } else if (option === CHANNEL_OPERATION_VIEW) {
        let thirdPartyLink = this.getLink(channel)
        if (thirdPartyLink && !thirdPartyLink.startsWith('http')) {
          thirdPartyLink = 'http://' + thirdPartyLink
        }
        window.open(thirdPartyLink)
      } else if (option === CHANNEL_OPERATION_HIDE) {
        this.handleDeleteChannel(channel)
      } else if (option === CHANNEL_OPERATION_ADD) {
        this.handleAddChannel(channel)
      } else if (option === CHANNEL_OPERATION_UPDATE) {
        this.handleUpdateChannel(channel)
      }
    },
    async setStepDataToJobInfo () {
      const stepData = this.stepData
      const jobInfo = this.jobInfo
      const postingChannel = []
      const channelAdditionInfoMap = {}
      _.forEach(this.channelList, channel => {
        const channelData = stepData[channel.id]
        const operationType = channelData?.operationType
        if (operationType) {
          postingChannel.push({
            id: channel.id,
            status: channelData.status,
            is_pending: channelData.isPending,
            operation_type: operationType,
            originStatus: channelData.originStatus,
            originOperationType: channelData.originOperationType
          })
        }
        const thirdPartyLink = channelData?.thirdPartyLink
        if (thirdPartyLink) {
          const thirdPartyLinkInfo = _.find(this.channelAdditionInfoMap[channel.id], { type: JOB_CHANNEL_ADDITIONAL_INFO_TYPE.THIRD_PARTY_LINK })
          if (thirdPartyLinkInfo) {
            channelAdditionInfoMap[channel.id] = [{
              ...thirdPartyLinkInfo,
              values: [{
                third_party_link: thirdPartyLink
              }]
            }]
          }
        }
      })
      this.$set(jobInfo, 'job_posting_channels', postingChannel)
      const infoTypeMap = {}
      let hasIndustry = false
      let hasSingleIndustry = false
      let hasIndustryPostingMapping = false
      _.forEach(this.additionalInfoList, info => {
        const infoLines = info?.infoLines
        if (!info?.show || !infoLines) {
          return
        }
        const channelId = info.channel.id
        _.forEach(infoLines, line => {
          const additionInfo = line._additionInfo
          const valueName = line._valueName
          const lineValue = line.value
          const infoTypeId = additionInfo.type_id
          const infoType = additionInfo.type
          let channelAdditionInfo = infoTypeMap[infoTypeId]
          if (!channelAdditionInfo) {
            infoTypeMap[infoTypeId] = channelAdditionInfo = {
              ...additionInfo,
              values: []
            }
            if (channelAdditionInfoMap[channelId]) {
              channelAdditionInfoMap[channelId].push(channelAdditionInfo)
            } else {
              channelAdditionInfoMap[channelId] = [channelAdditionInfo]
            }
          }
          const values = channelAdditionInfo.values
          if (infoType === JOB_CHANNEL_ADDITIONAL_INFO_TYPE.INDUSTRY) {
            if (line.hasIndustryPostingMapping) {
              hasIndustryPostingMapping = true
            }
            _.forEach(additionInfo.is_multiple ? lineValue : [lineValue], lineValueItem => {
              if (!lineValueItem || lineValueItem.length < 3) {
                return
              }
              values.push({
                industry_category: lineValueItem[0],
                industry_type: lineValueItem[1],
                industry_name: lineValueItem[2]
              })
            })
            if (values.length > 0 && values[0].industry_name) {
              hasIndustry = true
              if (values.length === 1) {
                hasSingleIndustry = true
              }
            }
          } else {
            _.forEach(additionInfo.is_multiple ? lineValue : [lineValue], lineValueItem => {
              let unsetValueItem = _.findLast(values, valueItem => !valueItem.hasOwnProperty(valueName))
              if (!unsetValueItem) {
                unsetValueItem = {}
                values.push(unsetValueItem)
              }
              unsetValueItem[valueName] = lineValueItem
            })
          }
        })
      })
      this.$set(jobInfo, 'job_posting_channels_additional_info', channelAdditionInfoMap)
      if (
        hasIndustryPostingMapping &&
        !this.matchedIndustryOption.noCustomField &&
        (this.hasChangedData || this.hasChangedAdditionInfo) &&
        hasIndustry &&
        hasSingleIndustry &&
        this.hasAnyPermission(['PERM_RPA_JOB_POSTING_MANAGEMENT', 'PREMIUM_RPA_JOB_POSTING'])
      ) {
        let confirm = false
        await this.$confirm(this.$t(`${this.i18nPrefix}.message.updateJobPostingIndustryMapping`), '', {
          confirmButtonText: this.$t(`${this.i18nPrefix}.btn.postingChannel.syncIndustry.yes`),
          cancelButtonText: this.$t(`${this.i18nPrefix}.btn.postingChannel.syncIndustry.no`)
        }).then(() => {
          confirm = true
        }).catch(() => {
          confirm = false
        })
        this.hasChangedAdditionInfo = false
        this.$set(jobInfo, 'synchronize_posting_industry_mapping', confirm)
      }
    },
    async getStepData () {
      this.loading = true
      await this.loadChannelList()
      await this.loadChannelAdditionalInfoMap()
      await this.loadMatchedIndustryOption()
      await this.loadChannelIndustryList()
      const stepData = {
      }
      const vm = this
      const jobInfo = this.jobInfo
      const additionalInfoList = []
      const channelAdditionInfo = jobInfo.job_posting_channels_additional_info || {}
      const matchedIndustryOption = this.matchedIndustryOption.matched
      if (!this.channelAdditionInfoMap) {
        const infoMap = {}
        _.forEach(channelAdditionInfo, (infoList, channelId) => {
          infoMap[channelId] = _.map(infoList, info => {
            info.values = null
            return info
          })
        })
        this.channelAdditionInfoMap = infoMap
      }
      const promiseList = _.map(this.channelList, async (channel) => {
        const channelId = channel.id
        const isDefault = channel.isDefault
        const stepChannel = {
          id: channelId,
          isPending: false,
          status: null,
          operationType: null,
          originStatus: null,
          originOperationType: null,
          thirdPartyLink: null
        }
        if (isDefault) {
          if (this.isEditMode) {
            stepChannel.originStatus = stepChannel.status = JOB_POSTING_STATUS.FINISHED
            stepChannel.originOperationType = stepChannel.operationType = [JOB_STATUS_TYPE.CLOSED, JOB_STATUS_TYPE.CANCEL].includes(this.jobInfo?.status) ? OPERATION_TYPE.DELETE : OPERATION_TYPE.ADD
          } else {
            stepChannel.operationType = OPERATION_TYPE.ADD
          }
        }
        stepData[channelId] = stepChannel
        const channelAdditionInfoList = channelAdditionInfo[channelId] || (this.channelAdditionInfoMap && this.channelAdditionInfoMap[channelId])
        if (!channelAdditionInfoList) {
          return
        }
        const thirdPartyLinkInfo = _.find(channelAdditionInfoList, { type: JOB_CHANNEL_ADDITIONAL_INFO_TYPE.THIRD_PARTY_LINK })
        if (thirdPartyLinkInfo) {
          if (thirdPartyLinkInfo.values && thirdPartyLinkInfo.values.length > 0) {
            let thirdPartyLink = thirdPartyLinkInfo.values[0].third_party_link
            if (thirdPartyLink && !thirdPartyLink.startsWith('http')) {
              thirdPartyLink = 'http://' + thirdPartyLink
            }
            stepChannel.thirdPartyLink = thirdPartyLink
          }
          if (channelAdditionInfoList.length === 1) {
            return
          }
        }
        const infoLines = {}
        additionalInfoList.push({
          get show () {
            return vm.getPostStatusClass(this.channel) !== 'disabled'
          },
          channel: channel,
          infoLines
        })

        const industryList = this.channelIndustryList[channelId]
        const matchedOptions = matchedIndustryOption[channelId]
        _.forEach(channelAdditionInfoList, info => {
          const infoType = info.type
          if (infoType === JOB_CHANNEL_ADDITIONAL_INFO_TYPE.THIRD_PARTY_LINK) {
            return
          }
          let infoValues = info.values || [{}]
          if (infoType === JOB_CHANNEL_ADDITIONAL_INFO_TYPE.INDUSTRY) {
            if (!industryList) {
              return
            }
            const hasValue = infoValues && infoValues[0] && infoValues[0].industry_name
            if (!hasValue && matchedOptions && matchedOptions.length > 0) {
              infoValues = _.map(matchedOptions, option => {
                return { ...option }
              })
            }
            const lineValue = _.map(infoValues, values => [
              (values?.industry_category && parseInt(values?.industry_category)) || null,
              (values?.industry_type && parseInt(values?.industry_type)) || null,
              (values?.industry_name && parseInt(values?.industry_name)) || null
            ])
            const infoLine = infoLines[infoType] = {
              _additionInfo: info,
              type: 'cascader',
              optionList: industryList,
              isMultiple: info.is_multiple,
              hasIndustryPostingMapping: info.has_industry_posting_mapping,
              props: {
                clearable: true,
                filterable: true,
                separator: ' --> ',
                disabled: this.disabled
              },
              value: info.is_multiple ? lineValue : lineValue[0]
            }
            createI18nProperty(infoLine, 'label', `${this.i18nPrefix}.label.channelAdditionalInfo.${infoType}`)
            createI18nProperty(infoLine, 'placeholder', `${this.i18nPrefix}.placeholder.channelAdditionalInfo.${infoType}`)
          } else {
            _.forEach(info.value_names, valueName => {
              const lineI18nKey = valueName
              let lineValue = info.is_multiple ? [] : null
              _.forEach(infoValues, values => {
                const value = values ? values[valueName] : null
                if (info.is_multiple) {
                  lineValue.push(value)
                } else {
                  lineValue = value
                  return false
                }
              })
              const infoLine = infoLines[valueName] = {
                _additionInfo: info,
                _valueName: valueName,
                value: lineValue
              }
              createI18nProperty(infoLine, 'label', `${this.i18nPrefix}.label.channelAdditionalInfo.${lineI18nKey}`, null, lineI18nKey)
              createI18nProperty(infoLine, 'placeholder', `${this.i18nPrefix}.placeholder.channelAdditionalInfo.${lineI18nKey}`, null, '')
            })
          }
        })
      })
      await Promise.all(promiseList)
      _.forEach(jobInfo.job_posting_channels, postingChannel => {
        const isDefault = postingChannel.id === DEFAULT_CHANNEL_ID
        if (postingChannel.operation_type === OPERATION_TYPE.DELETE && !isDefault && postingChannel.status === JOB_POSTING_STATUS.FINISHED) {
          return
        }
        const status = postingChannel.hasOwnProperty('status') ? postingChannel.status : (postingChannel.is_pending ? JOB_POSTING_STATUS.PENDING : JOB_POSTING_STATUS.FINISHED)
        const originStatus = postingChannel.hasOwnProperty('originStatus') ? postingChannel.originStatus : status
        const operationType = postingChannel.operation_type
        const originOperationType = postingChannel.hasOwnProperty('originOperationType') ? postingChannel.originOperationType : operationType
        _.assign(stepData[postingChannel.id], {
          status,
          originStatus,
          operationType,
          originOperationType,
          isPending: postingChannel.is_pending
        })
      })
      this.additionalInfoList = additionalInfoList
      this.hasChangedData = false
      this.loading = false
      return stepData
    },
    onAdditionalInfoChange () {
      this.hasChangedAdditionInfo = true
    },
    handleLineItemDelete (line, itemValue, itemIndex) {
      const deleted = line.value.splice(itemIndex, 1)
      const hasData = _.findIndex(deleted, item => item && item[0]) > -1
      if (hasData) {
        this.hasChangedAdditionInfo = true
      }
    },
    handleLineItemAdd (line) {
      const additionInfo = line._additionInfo
      const infoType = additionInfo.type
      if (infoType === JOB_CHANNEL_ADDITIONAL_INFO_TYPE.INDUSTRY) {
        line.value.push([])
      } else {
        line.value.push(null)
      }
    },
    getLink (channel) {
      return this.stepData[channel.id].thirdPartyLink
    }
  },
  watch: {
    show: {
      immediate: true,
      handler () {
        this.refreshLastChannelEl()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-post-channel {
  .processing-message {
    padding-left: 15px;

    &::before {
      content: '*';
      color: #f56c6c;
      margin-right: 5px;
      font-size: 14px;
      vertical-align: middle;
    }

    + .channel-list {
      &::before {
        content: "";
        display: block;
        width: 100%;
        height: 100%;
        cursor: not-allowed;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 2;
      }
    }
  }

  .channel-list {
    display: flex;
    align-items: center;
    flex-flow: row wrap;
    position: relative;

    .channel-icon {
      color: #606266;;
    }

    .channel {
      margin: 10px 0px;
      width: 25%;
      position: relative;

      &:hover {
        .channel-wrapper {
          .post-btn-group {
            transform: translateY(0);
          }
        }
      }

      &.finished {
        .channel-wrapper {

          .channel-icon {
            border-color: #51BF82;

          }
          .status-icon {
            color: #51BF82;
            &::before {
              content: "\e72a";
            }
          }
        }
      }

      &.add {
        .channel-wrapper {
          .channel-icon {
            border-color: #F4A958;

          }
          .status-icon {
            color: #F4A958;
            &::before {
              content: "\e72c";
            }
          }
        }
      }

      &.update {
        .channel-wrapper {
          .channel-icon {
            border-color: #0896dd;

          }
          .status-icon {
            color: #0896dd;
            &::before {
              content: "\e729";
            }
          }
        }
      }

      &.delete {
        .channel-wrapper {
          .channel-icon {
            border-color: #EA7F7F;

          }
          .status-icon {
            color: #EA7F7F;
            &::before {
              content: "\e72b";
            }
          }
        }
      }

      &.disabled {
        .channel-wrapper {
          .channel-icon {
            filter: grayscale(1);
          }
        }
      }

      .channel-wrapper {
        padding: 5px;
        // overflow: hidden;
        width: 98px;
        margin: auto;
        position: relative;

        .channel-icon {
          font-size: 85px;
          border-radius: 50%;
          padding: 2px;
          border: 2px solid #fff;
        }

        .status-icon {
          position: absolute;
          top: 0;
          right: 0;
          width: 18px;
          height: 16px;
          display: inline-block;
          font-family: iconfont!important;
          font-style: normal;
          font-size: 18px;
          font-variant: normal;
          text-transform: none;
          -webkit-font-smoothing: antialiased;

          &.none {
            display: none;
          }
        }

        .post-btn-dropdown-container {
          cursor: pointer;
          position: absolute;
          top: 0;
          right: -25px;

          .operate-icon {
          }
        }
      }

      .channel-name {
        text-align: center;
      }
    }
  }

  .tip-message {
    margin-top: 10px;
    padding-left: 15px;
  }

  .additional-info-container {
    margin-top: 20px;
    padding-left: 15px;

    .container-title {
      font-size: 16px;
    }

    .container-body {
      .info-editor {
        margin-top: 15px;

        .channel-name-container {
          display: flex;
          padding-bottom: 5px;
          border-bottom: 1px solid #cccccc;
          align-items: center;

          .channel-icon {
            width: 65px;
            height: 35px;
          }

          .channel-name {
            margin-left: 15px;
            font-size: 14px;
          }
        }

        .additional-info {
          width: 85%;
          margin-top: 15px;

          .info-line {
            display: flex;
            align-content: center;

            &:not(:first-child) {
              margin-top: 15px;
            }

            .line-label {
              width: 100px;
              padding-right: 20px;
              line-height: 32px;
              font-size: 14px;
            }

            .line-content {
              flex: 1;

              ::v-deep {
                .el-input {
                  .el-input__inner {
                    height: 32px;
                    line-height: 32px;
                    padding: 0 5px;
                    border-radius: 0;
                    color: #232323;

                    &:hover, &:focus {
                      border-color: #C0C4CC;
                    }
                  }
                }

                .el-input--suffix {
                  .el-input__inner {
                    padding-right: 30px;
                  }
                }

                .el-cascader {
                  line-height: 32px;
                }

                .el-cascader, .el-select {
                  width: 100%;

                  .el-input {
                    .el-input__icon {
                      line-height: inherit;
                    }

                    &.is-focus {
                      .el-input__inner {
                        border-color: #409EFF;
                      }
                    }
                  }
                  .el-select__input {
                    margin-left: 5px;
                  }
                }
              }

              .content-item-container {
                margin-bottom: 10px;

                .content-item {
                  display: flex;
                  margin-bottom: 10px;
                  justify-content: space-between;
                  align-items: center;

                  .delete-btn {
                    margin-left: 10px;
                    font-size: 20px;
                    cursor: pointer;
                  }
                }
              }

              .add-btn {
                padding: 0;
                border: none;
              }
            }
          }
        }
      }
    }
  }

  .channel-icon {
    background-repeat: no-repeat;
    background-position: center;
    background-size: 100%;

    &.channel-ajinga {
      background-image: url(~@/assets/image/postingChannel/ajinga_cn.png);
    }

    &.channel-51job {
      background-image: url(~@/assets/image/postingChannel/51job.png);
    }

    &.channel-wotui {
      background-image: url(~@/assets/image/postingChannel/wotui.png);
    }

    &.channel-zhaopin {
      background-image: url(~@/assets/image/postingChannel/zhaopin.png);
    }

    &.channel-liepin {
      background-image: url(~@/assets/image/postingChannel/liepin.png);
    }

    &.channel-58 {
      background-image: url(~@/assets/image/postingChannel/58.png);
    }

    &.channel-ganji {
      background-image: url(~@/assets/image/postingChannel/ganji.png);
    }

    &.channel-veryeast {
      background-image: url(~@/assets/image/postingChannel/veryeast.png);
    }

    &.channel-shixiseng {
      background-image: url(~@/assets/image/postingChannel/shixiseng.png);
    }

    &.channel-linkedin {
      background-image: url(~@/assets/image/postingChannel/linkedin.png);
    }

    &.channel-jobstreet {
      background-image: url(~@/assets/image/postingChannel/jobstreet.png);
    }

    &.channel-jobsdb {
      background-image: url(~@/assets/image/postingChannel/jobsdb.png);
    }

    &.channel-zzjob88 {
      background-image: url(~@/assets/image/postingChannel/zzjob88.png);
    }

    &.channel-104 {
      background-image: url(~@/assets/image/postingChannel/104.jpg);
      background-size: 130%;
      background-position: -25px 50%;
    }

    &.channel-indeed {
      background-image: url(~@/assets/image/postingChannel/indeed.jpg);
    }

    &.channel-parttime {
      background-image: url(~@/assets/image/postingChannel/parttime.png);
      background-size: unset;
    }

    &.channel-lagou {
      background-image: url(~@/assets/image/postingChannel/lagou.png);
      background-size: 90%;
    }

    &.channel-boss {
      background-image: url(~@/assets/image/postingChannel/boss.png);
      background-size: 90%;
    }

    &.channel-hrol {
      background-image: url(~@/assets/image/postingChannel/hrol.png);
      background-size: 90%;
    }

    &.channel-91job {
      background-image: url(~@/assets/image/postingChannel/91job.png);
      background-size: 90%;
    }

    &.channel-yimaitongdao {
      background-image: url(~@/assets/image/postingChannel/yimaitongdao.svg);
    }

    &.channel-liepin_agency {
      background-image: url(~@/assets/image/postingChannel/liepin_agency.png);
    }

    &.channel-maimai {
      background-image: url(~@/assets/image/postingChannel/maimai.png);
      background-size: 90%;
    }

    &.channel-gooood {
      background-image: url(~@/assets/image/postingChannel/gooood.jpg);
      background-size: 90%;
    }
  }
}
.page-en {
  .job-post-channel {
    .channel-list {
      .channel{
        .channel-wrapper {
          .channel-icon {
            &.channel-ajinga {
              background-image: url(~@/assets/image/postingChannel/ajinga_en.png);
            }
          }
        }
      }
    }
  }
}
</style>
